import React, { useState, useEffect } from 'react';
import { User, Edit3, Check, X, Mail, Phone, FileText, Award, CreditCard } from 'lucide-react';
import Card from '../../../components/ui/card';
import Button from '../../../components/ui/button';
import Input from '../../../components/ui/input';

const PersonalInfoCard = ({ client, clientDataHook, currentStep }) => {
	const { updateCard, isCardEditMode, toggleCardEditMode, cancelCardEdit, isOnboarding, refreshData, isLoading } = clientDataHook;
	const cardName = 'personalInfo';
	const isEditing = isOnboarding || isCardEditMode(cardName);
	
	// Form state for all fields
	const [formValues, setFormValues] = useState({
		firstName: client?.firstName || client?.first_name || '',
		lastName: client?.lastName || client?.last_name || '',
		designations: client?.designations || '',
		licenseNumber: client?.licenseNumber || client?.license_number || '',
		certifications: client?.certifications || '',
		email: client?.email || '',
		phone: client?.phone || ''
	});

	// Refresh data when component mounts or step changes (for onboarding)
	useEffect(() => {
		if (isOnboarding && refreshData && currentStep !== undefined) {
			console.log('🟦 PersonalInfoCard - Component mounted/step changed, refreshing data. Step:', currentStep);
			refreshData();
		}
	}, [isOnboarding, currentStep]); // Remove refreshData from dependencies to prevent infinite loop

	// Update form values when client data changes
	useEffect(() => {
		console.log('🟦 PersonalInfoCard - client data received:', client);
		console.log('🟦 PersonalInfoCard - client prop reference changed, updating form values');
		console.log('🟦 PersonalInfoCard - specific fields:', {
			firstName: client?.firstName,
			first_name: client?.first_name,
			lastName: client?.lastName,
			last_name: client?.last_name,
			licenseNumber: client?.licenseNumber,
			license_number: client?.license_number,
			designations: client?.designations,
			certifications: client?.certifications,
			email: client?.email,
			phone: client?.phone
		});
		
		const newFormValues = {
			firstName: client?.firstName || client?.first_name || '',
			lastName: client?.lastName || client?.last_name || '',
			designations: client?.designations || '',
			licenseNumber: client?.licenseNumber || client?.license_number || '',
			certifications: client?.certifications || '',
			email: client?.email || '',
			phone: client?.phone || ''
		};
		
		console.log('🟦 PersonalInfoCard - Setting form values to:', newFormValues);
		setFormValues(newFormValues);
	}, [client]);

	const fields = [
		{
			key: 'firstName',
			label: 'First Name',
			type: 'text',
			icon: User,
			placeholder: 'Enter first name',
			required: true
		},
		{
			key: 'lastName',
			label: 'Last Name',
			type: 'text',
			icon: User,
			placeholder: 'Enter last name',
			required: true
		},
		{
			key: 'email',
			label: 'Email',
			type: 'email',
			icon: Mail,
			placeholder: 'Enter email address',
			required: true
		},
		{
			key: 'phone',
			label: 'Phone',
			type: 'tel',
			icon: Phone,
			placeholder: 'Enter phone number',
			required: false
		},
		{
			key: 'designations',
			label: 'Designation (e.g., REALTOR®, Broker, Property Manager)',
			type: 'text',
			icon: Award,
			placeholder: 'e.g., REALTOR®, Broker, Property Manager',
			required: false
		},
		{
			key: 'licenseNumber',
			label: 'License/DRE #',
			type: 'text',
			icon: CreditCard,
			placeholder: 'License or DRE number',
			required: false
		},
		{
			key: 'certifications',
			label: 'Certifications (if applicable)',
			type: 'text',
			icon: FileText,
			placeholder: 'Professional certifications...',
			required: false
		}
	];

	// Handle form field changes
	const handleFieldChange = (fieldKey, value) => {
		setFormValues(prev => ({
			...prev,
			[fieldKey]: value
		}));
	};

	// Validate form data
	const validateForm = () => {
		const errors = [];
		
		if (!formValues.firstName.trim()) {
			errors.push('First name is required');
		}
		if (!formValues.lastName.trim()) {
			errors.push('Last name is required');
		}
		if (!formValues.email.trim()) {
			errors.push('Email is required');
		} else if (!/\S+@\S+\.\S+/.test(formValues.email)) {
			errors.push('Email is invalid');
		}
		
		// Phone validation (optional)
		if (formValues.phone && !validatePhone(formValues.phone)) {
			errors.push('Phone number is invalid');
		}
		
		return errors;
	};

	// Phone validation function
	const validatePhone = (phone) => {
		if (!phone || phone.trim() === '') return true;
		const phoneRegex = /^[\+]?[1-9][\d]{0,15}$|^[\+]?[(]?[\d\s\-\(\)\.]{10,}$/;
		return phoneRegex.test(phone.replace(/[\s\-\(\)\.]/g, ''));
	};

	// Handle save
	const handleSave = async () => {
		const errors = validateForm();
		if (errors.length > 0) {
			alert('Please fix the following errors:\n' + errors.join('\n'));
			return;
		}

		const success = await updateCard(formValues);
		if (success) {
			toggleCardEditMode(cardName); // Exit edit mode
		}
	};

	// Handle cancel
	const handleCancel = () => {
		// Reset form values to original client data
		setFormValues({
			firstName: client?.firstName || client?.first_name || '',
			lastName: client?.lastName || client?.last_name || '',
			designations: client?.designations || '',
			licenseNumber: client?.licenseNumber || client?.license_number || '',
			certifications: client?.certifications || '',
			email: client?.email || '',
			phone: client?.phone || ''
		});
		cancelCardEdit(cardName);
	};

	return (
		<Card>
			{!isOnboarding && (
				<Card.Header className='flex flex-row items-center justify-center space-y-0 pb-4'>
					<div className='space-y-1 text-center'>
						<Card.Title className='flex items-center justify-center gap-2 text-lg font-montserrat-medium text-gray-900'>
							<User className='h-5 w-5' />
							Personal Information
						</Card.Title>
						<Card.Description className='text-sm text-gray-600'>
							Your basic professional information and contact details.
						</Card.Description>
					</div>
					{!isEditing && (
						<Button
							size='sm'
							variant='outline'
							onClick={() => toggleCardEditMode(cardName)}
							className='border-gray-300 text-gray-600 hover:bg-gray-50 ml-4'>
							<Edit3 className='h-4 w-4 mr-2' />
							Edit
						</Button>
					)}
				</Card.Header>
			)}
			<Card.Content className='space-y-4'>
				{fields.map((field) => (
					<div key={field.key} className='space-y-2'>
						<label className='text-sm font-semibold text-gray-800'>
							{field.label}
							{field.required && <span className='text-red-500 ml-1'>*</span>}
						</label>
						
						{isEditing ? (
							<Input
								type={field.type}
								value={formValues[field.key] || ''}
								onChange={(e) => handleFieldChange(field.key, e.target.value)}
								placeholder={field.placeholder}
								className='w-full'
							/>
						) : (
							<div className='flex items-start gap-2'>
								<field.icon className='h-4 w-4 text-gray-500 mt-0.5' />
								<div className='flex-1'>
									<span className='text-sm font-montserrat-regular text-gray-900'>
										{formValues[field.key] || (
											<span className='text-gray-400 italic'>
												{field.placeholder}
											</span>
										)}
									</span>
								</div>
							</div>
						)}
					</div>
				))}

				{/* Save/Cancel buttons when editing */}
				{isEditing && !isOnboarding && (
					<div className='flex justify-end gap-3 pt-4 border-t border-gray-200'>
						<Button
							variant='outline'
							onClick={handleCancel}
							className='border-gray-300 text-gray-600 hover:bg-gray-50'>
							<X className='h-4 w-4 mr-2' />
							Cancel
						</Button>
						<Button
							onClick={handleSave}
							className='bg-gray-900 hover:bg-gray-800 text-white'>
							<Check className='h-4 w-4 mr-2' />
							Save Changes
						</Button>
					</div>
				)}
				
				{/* Save button for onboarding */}
				{isOnboarding && (
					<div className='pt-6 mt-6 border-t border-gray-200'>
						<Button
							onClick={handleSave}
							className='w-full bg-gray-900 hover:bg-gray-800 text-white'>
							<Check className='h-4 w-4 mr-2' />
							Save & Continue
						</Button>
					</div>
				)}
			</Card.Content>
		</Card>
	);
};

export default PersonalInfoCard;